import { useEffect, useState } from "react";
import { isCocoplusApp } from "~/lib/applicationId";
import { native } from "~/lib/nativeBridge";
import { isWebView } from "~/lib/webview";
import { useAuthStore } from "~/stores/useAuthStore";
import { isVersionBelow } from "~/utils/compareVersion";

/** coco+(145) の強制アップデートが必要な最低バージョン */
const MINIMUM_REQUIRED_VERSION = "1.1";

/**
 * coco+(145) アプリのバージョンが最低要件を満たしているかを確認し、
 * 満たしていない場合は強制アップデートゲートを表示すべきかを返す。
 *
 * 条件:
 * - 自動ログイン完了（useAuthStore の token 確定）後であること
 * - coco+(applicationId=145) かつ WebView 環境
 * - appVersion が取得でき、かつ MINIMUM_REQUIRED_VERSION 未満
 *
 * 上記以外（未ログイン / ブラウザ / バージョン取得失敗 / 非coco+ / 新バージョン）は
 * 絶対に true を返さない。自動ログインより先に出さないことでログイン画面への
 * 被りとブリッジ競合を避ける。
 */
export const useCocoplusForceUpdate = (): boolean => {
  const [shouldBlock, setShouldBlock] = useState(false);
  // 自動ログイン完了（token 確定）後にのみ判定する。
  // ログイン画面への被り・自動ログイン中のブリッジ競合を避けるため。
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    // ログイン未完了（token 未確定）の間は判定しない
    if (!token) return;

    // coco+(145) かつ WebView 環境のときのみバージョンチェックを行う
    if (!isCocoplusApp() || !isWebView()) return;

    void (async () => {
      try {
        const identifiers = await native.getLoginIdentifiers();
        const { appVersion } = identifiers;

        // appVersion が取得できなかった場合はブロックしない
        if (!appVersion) return;

        if (isVersionBelow(appVersion, MINIMUM_REQUIRED_VERSION)) {
          setShouldBlock(true);
        }
      } catch {
        // バージョン取得失敗時はブロックしない（安全側に倒す）
      }
    })();
  }, [token]);

  return shouldBlock;
};
